import {DxPopupComponent} from 'devextreme-angular/ui/popup';
import {isVoid} from '../utils';

// ORION window chrome controller (CR-20260830-001 zoom + CR HicgA7n5 containment), extracted from
// HedgingGridComponent so all three ORION hosts (Hedging Grid / OPG / APG) share ONE mechanism
// (owner 2026-08-31: "this zoom hatch needs to be implemented for every copilot").
//
// The whole window (title bar included) shrinks via `transform: translate(...) scale(...)` on
// .dx-overlay-content. NOT CSS `zoom` — zoom reflows layout, and the owner's round-1 smoke showed
// DX's px-measured geometry does not follow it: the frame kept its full-size box while descendants
// shrank (gap + scrollbar), drag clamped to the stale full-size footprint, and the title toolbar
// ellipsized and never recovered. A scale transform is purely visual (no reflow), so DX's layout
// math is untouched at every level. DX owns the translate() (drag positioning) and REWRITES the
// inline transform on every drag frame, so our scale() is composed after it and re-asserted by a
// guarded MutationObserver. That same observer is the single choke point where the viewport clamp
// runs — it fires on EVERY inline-style write (DX title-drag frames, the ear drag's translate
// rewrites, position reseeds), so both drag paths are clamped frame by frame.
//
// The host owns the Angular side: template bindings delegate to a thin wrapper method per action
// (@DetectMethodChanges lives on the wrappers — the hosts are OnPush), calls apply() from the
// popup's onShown, clampNow() from its window-resize hook, and dispose() from ngOnDestroy.
export class OrionWindowController {

    // Zoom numbers are PLACEHOLDERS pending the owner's hands-on tuning (owner 2026-08-30:
    // "we'll pick the number later ... I'll tell you exact numbers").
    private static readonly ZOOM_STEP = 0.1;
    private static readonly ZOOM_MIN = 0.3;              // floor — "stop at ~30% of the original"
    private static readonly ZOOM_MAX = 1.0;              // only shrinking was requested

    // Session-only by ruling — no persistence. Survives popup close/reopen (element stays in DOM).
    zoomLevel = 1;

    private _observer: MutationObserver = null;
    private _observedEl: HTMLElement = null;

    // The popup arrives via a getter because the ViewChild resolves after construction and the
    // element can be recreated by the host's *ngIf.
    constructor(private readonly _getPopup: () => DxPopupComponent) {
    }

    get zoomPct(): number {
        return Math.round(this.zoomLevel * 100);
    }

    zoomIn(): void {
        this.setZoom(this.zoomLevel + OrionWindowController.ZOOM_STEP);
    }

    zoomOut(): void {
        this.setZoom(this.zoomLevel - OrionWindowController.ZOOM_STEP);
    }

    // Double-click on the strip snaps back to 100% (owner round 4). Bound on the STRIP because the
    // drag handler's pointer capture retargets the compat click/dblclick events to the capturing
    // element — a binding on the .pct child never receives them. The +/- buttons are excluded so
    // two fast zoom steps never read as a reset.
    onStripDblClick(ev: MouseEvent): void {
        const target = ev.target as HTMLElement;
        if (target && target.closest('.btn')) {
            return;
        }
        this.setZoom(OrionWindowController.ZOOM_MAX);
    }

    private setZoom(level: number): void {
        // One-decimal rounding keeps the 0.1 stepping free of float drift (0.7000000001 etc.).
        const rounded = Math.round(level * 10) / 10;
        this.zoomLevel = Math.min(
            OrionWindowController.ZOOM_MAX,
            Math.max(OrionWindowController.ZOOM_MIN, rounded)
        );
        this.apply();
    }

    private _overlay(): HTMLElement | null {
        const content = this._getPopup()?.instance?.content();
        return content?.parentElement || null;
    }

    // (Re)asserts the scale + containment machinery on the current overlay element. Call on every
    // popup onShown: the overlay element can be recreated when the popup's *ngIf re-mounts it, and
    // the edge strip must be (re)parented onto the overlay.
    apply(): void {
        const overlay = this._overlay();
        if (isVoid(overlay)) {
            return;
        }
        // Top-RIGHT anchor (owner round 4): the zoom controls hang off the right edge, so pinning
        // the right corner keeps them stationary under the mouse across repeated zoom clicks.
        overlay.style.transformOrigin = 'top right';
        this._syncScale(overlay);
        // Re-target the observer whenever the element changes. Observing an attribute we also
        // write is loop-safe because both callees write only when a change is actually needed.
        if (this._observedEl !== overlay) {
            if (this._observer) {
                this._observer.disconnect();
            }
            this._observer = new MutationObserver(() => {
                this._syncScale(overlay);
                this._clampIntoView(overlay);
            });
            this._observer.observe(overlay, {attributes: true, attributeFilter: ['style']});
            this._observedEl = overlay;
        }
        this._clampIntoView(overlay);   // rescue an already-off-screen window on open/zoom
        // Reparent the edge strip onto the overlay so `left: 100%` resolves against the WINDOW edge
        // and popup-content padding/scroll cannot clip it. Located by class, not @ViewChild — the
        // strip lives inside a *dxTemplate, whose views DX instantiates outside Angular's view-query
        // reach. Angular bindings follow the node when it moves.
        const strip = overlay.querySelector<HTMLElement>('.orion-zoom-strip');
        if (!isVoid(strip) && strip.parentElement !== overlay) {
            overlay.appendChild(strip);
        }
    }

    // The zoom sidebar doubles as the window's "ear" drag handle (owner round 3: at deep zoom the
    // title bar is tiny and hard to grab; the strip never shrinks). We drag by rewriting the
    // translate() in the overlay's inline transform — the same slot DX's own title-drag writes —
    // in SCREEN px (translate composes before our scale, so deltas are 1:1 at any zoom). DX's
    // internal position state does not learn of the move, same as its own drag: the host re-seeds
    // the position option on every open anyway. setPointerCapture + pointercancel per the platform
    // pointer-drag recipe; +/- buttons are excluded so their clicks keep working.
    onStripPointerDown(ev: PointerEvent): void {
        const target = ev.target as HTMLElement;
        if (target && target.closest('.btn')) {
            return;
        }
        const overlay = this._overlay();
        const strip = ev.currentTarget as HTMLElement;
        if (isVoid(overlay) || isVoid(strip)) {
            return;
        }
        // No preventDefault here: canceling pointerdown suppresses the browser's compatibility
        // mouse events, which would kill the dblclick-to-reset on the percent readout. Text
        // selection is blocked by user-select:none on the strip instead.
        const startX = ev.clientX;
        const startY = ev.clientY;
        const m = /translate\((-?[\d.]+)px,\s*(-?[\d.]+)px\)/.exec(overlay.style.transform || '');
        const baseTx = m ? parseFloat(m[1]) : 0;
        const baseTy = m ? parseFloat(m[2]) : 0;
        const onMove = (e: PointerEvent) => {
            const tx = baseTx + (e.clientX - startX);
            const ty = baseTy + (e.clientY - startY);
            const current = overlay.style.transform || '';
            overlay.style.transform = /translate\([^)]*\)/.test(current)
                ? current.replace(/translate\([^)]*\)/, `translate(${tx}px, ${ty}px)`)
                : `translate(${tx}px, ${ty}px) ${current}`.trim();
        };
        const onDone = (e: PointerEvent) => {
            strip.releasePointerCapture(e.pointerId);
            strip.removeEventListener('pointermove', onMove);
            strip.removeEventListener('pointerup', onDone);
            strip.removeEventListener('pointercancel', onDone);
        };
        strip.setPointerCapture(ev.pointerId);
        strip.addEventListener('pointermove', onMove);
        strip.addEventListener('pointerup', onDone);
        strip.addEventListener('pointercancel', onDone);
    }

    // Re-clamp hook for the host's window-resize handler (browser-zoom changes fire resize too).
    clampNow(): void {
        const overlay = this._overlay();
        if (!isVoid(overlay)) {
            this._clampIntoView(overlay);
        }
    }

    dispose(): void {
        if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
        }
        this._observedEl = null;
    }

    // Compose `scale(level)` AFTER whatever transform DX put there (translate first, then scale =
    // drag deltas stay 1:1 and the shrink anchors to the translated box's corner). Any
    // pre-existing scale() (ours, or a show-animation frame's) is replaced, not stacked.
    private _syncScale(overlay: HTMLElement): void {
        const current = overlay.style.transform || '';
        const stripped = current.replace(/\s*scale\([^)]*\)/g, '').trim();
        const desired = this.zoomLevel === 1
            ? stripped
            : `${stripped} scale(${this.zoomLevel})`.trim();
        if (current !== desired) {
            overlay.style.transform = desired;
        }
    }

    // CR HicgA7n5: keep the window FULLY inside the viewport (the behaviour of every other dialog,
    // owner smoke 2026-08-31). Works on the RENDERED box (getBoundingClientRect reflects translate
    // AND scale), and corrects by adjusting the translate() slot — corrections are 1:1 screen px
    // at any zoom (same reasoning as the ear drag). Writes only when out of bounds, so the
    // observer settles in one pass. Bottom/right corrections run first so that when the window is
    // larger than the viewport, the later top/left corrections win — the title bar stays reachable.
    private _clampIntoView(overlay: HTMLElement): void {
        const rect = overlay.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
            return;   // popup hidden (display:none) — nothing to clamp
        }
        let dx = 0;
        let dy = 0;
        if (rect.bottom > window.innerHeight) {
            dy = window.innerHeight - rect.bottom;   // spills past the bottom — pull up
        }
        if (rect.top + dy < 0) {
            dy = -rect.top;                          // above the viewport (or oversized) — pull down; wins
        }
        if (rect.right > window.innerWidth) {
            dx = window.innerWidth - rect.right;     // spills past the right — pull left
        }
        if (rect.left + dx < 0) {
            dx = -rect.left;                         // past the left (or oversized) — pull right; wins
        }
        if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
            return;
        }
        const current = overlay.style.transform || '';
        const m = /translate\((-?[\d.]+)px,\s*(-?[\d.]+)px\)/.exec(current);
        const tx = (m ? parseFloat(m[1]) : 0) + dx;
        const ty = (m ? parseFloat(m[2]) : 0) + dy;
        overlay.style.transform = /translate\([^)]*\)/.test(current)
            ? current.replace(/translate\([^)]*\)/, `translate(${tx}px, ${ty}px)`)
            : `translate(${tx}px, ${ty}px) ${current}`.trim();
    }
}
